// Zugriff auf die UI-API.
//
// Eine Stelle, an der der Anmelde-Header gesetzt wird. Stünde er an jedem
// Aufruf, wäre der erste, der ihn vergisst, ein 401 ohne erkennbaren Grund.

import type { Ref } from '../components/common/Refs'

import { authHeader } from './auth'

/** Alles unter diesem Pfad ist für Menschen und läuft über Basic Auth. */
const BASE = '/api/ui/v1'

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
  }
}

/** Der Server war nicht erreichbar — kein Statuscode, also auch kein 401. */
export class NetworkError extends Error {}

/** Der Aufruf wurde abgebrochen — kein Fehler, sondern eine Entscheidung. */
export class Aborted extends Error {}

/**
 * Holt den lesbaren Satz aus dem `detail` einer Fehlerantwort.
 *
 * Das Backend antwortet im Format von FastAPI — einer LISTE aus Ort und
 * Meldung, damit ein Aufrufer weiß, welches Feld gemeint ist:
 *
 *     {"detail": [{"loc": ["body", "query"], "msg": "Der Suchbegriff …"}]}
 *
 * Diese Struktur ungeprüft durch `JSON.stringify` zu schicken hieß, dem
 * Benutzer beim Zurücklöschen eines Suchfeldes eckige Klammern und
 * Feldnamen hinzulegen. Genau das stand vorher auf dem Schirm.
 *
 * Gibt `null` zurück, wenn nichts Lesbares drinsteht — dann bleibt es beim
 * Statustext. Eine rohe Struktur ist NIE eine Meldung für Menschen; lieber
 * "Bad Request" als `[{"loc":…}]`.
 */
function fehlertext(detail: unknown): string | null {
  if (typeof detail === 'string' && detail.trim()) return detail

  if (Array.isArray(detail)) {
    const saetze = detail
      .map((eintrag) => {
        if (typeof eintrag === 'string') return eintrag
        const msg = (eintrag as { msg?: unknown } | null)?.msg
        return typeof msg === 'string' ? msg : null
      })
      .filter((satz): satz is string => Boolean(satz && satz.trim()))
    // Mehrere Befunde als ein Satz: Sie stehen in einer Zeile über der
    // Tabelle, und eine Aufzählung bräuchte dort Platz, den es nicht gibt.
    if (saetze.length) return saetze.join(' ')
  }

  const msg = (detail as { msg?: unknown } | null)?.msg
  if (typeof msg === 'string' && msg.trim()) return msg

  return null
}

/**
 * Wie viele Abrufe gleichzeitig laufen, und wie oft ein 429 wiederholt wird.
 *
 * Der Server begrenzt je Adresse — 300 Abrufe je Minute mit kleinem Puffer.
 * „Alle Ideen auf" schickte vierzig Abrufe in einem Schlag, und die Hälfte
 * kam als 429 zurück; der Feed lud zwanzig Details auf einmal und blieb
 * leer. Sechs auf einmal halten den Puffer, und auf ein 429 wird gewartet
 * und neu versucht statt aufgegeben.
 *
 * NUR HIER, nicht am Ereignisstrom: Der hält eine Leitung offen und darf
 * keinen Platz belegen.
 */
const MAX_GLEICHZEITIG = 6
const VERSUCHE = 3

let laufend = 0
const wartend: (() => void)[] = []

function platz(): Promise<void> {
  if (laufend < MAX_GLEICHZEITIG) { laufend++; return Promise.resolve() }
  return new Promise((frei) => wartend.push(() => { laufend++; frei() }))
}

function freigeben(): void {
  laufend--
  wartend.shift()?.()
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const header = authHeader()

  let response: Response
  for (let versuch = 1; ; versuch++) {
    await platz()
    try {
      response = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        ...(init?.headers ?? {}),
        ...(header ? { Authorization: header } : {}),
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      },
    })
    } catch (cause) {
      freigeben()
      // Ein Abbruch sieht wie ein Netzwerkfehler aus, ist aber keiner: Er wurde
      // ausgeloest, weil die Antwort nicht mehr gebraucht wird. Als Fehler
      // gemeldet stuende beim Tippen "Server nicht erreichbar" auf dem Schirm.
      if (init?.signal?.aborted) throw new Aborted()
      // Ein Netzwerkfehler ist KEIN 401. Beides gleich zu behandeln hieße: Bei
      // abgeschaltetem Server stünde "Benutzer oder Kennwort falsch" auf dem
      // Formular, und der Anmeldende sucht den Fehler bei sich.
      throw new NetworkError(String(cause))
    }
    freigeben()

    if (response.status !== 429 || versuch >= VERSUCHE || init?.signal?.aborted) break
    // Warten, wie der Server es sagt — sonst eine, dann zwei Sekunden, mit
    // etwas Streuung, damit nicht alle Wartenden gleichzeitig wiederkommen.
    const sekunden = Number(response.headers.get('Retry-After')) || versuch
    await new Promise((weiter) => setTimeout(weiter, sekunden * 1000 + Math.random() * 400))
  }

  if (!response.ok) {
    let detail = response.statusText
    try {
      const body = await response.json()
      detail = fehlertext(body.detail) ?? detail
    } catch {
      /* Antwort ohne JSON-Körper — dann bleibt der Statustext */
    }
    throw new ApiError(response.status, detail)
  }

  // 204 hat keinen Körper. `json()` scheiterte daran, und die Seite meldete
  // einen Fehler für eine Löschung, die längst angekommen war.
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

/** Wer angemeldet ist, samt Rollen und Rechten. */
export interface Session {
  user: string
  type: string
  roles: string[]
  permissions: string[]
}

/** Ein Instrument, wie die Liste es braucht. */
export interface Instrument {
  id: number
  isin: string
  name: string
  /** 'S' Aktie, 'E' ETF, 'I' Index. Fehlt bei von Hand angelegten. */
  instrument_type?: string | null
  primary_exchange: string

  /**
   * Die Einordnung, je Norm als Paar aus Code und Name.
   *
   * Die Tabelle zeigt den NAMEN, gefiltert und verknüpft wird über den CODE.
   * Beide dürfen fehlen, und das bedeutet Verschiedenes: Code leer heißt
   * „nicht eingeordnet" — bei ETFs der Regelfall, denn ein Fonds hat keinen
   * eigenen Geschäftszweck. Code gesetzt und Name leer heißt, dass der Code
   * auf keinen Eintrag im Branchenbaum zeigt: ein Datenfehler.
   */
  sector_code?: string | null
  /** GICS-Sektor im Klartext. Englisch — die amtliche Bezeichnung. */
  sector_name?: string | null
  sic_code?: string | null
  /** SIC-Branche im Klartext. Aus der Anmeldung bei der US-Börsenaufsicht. */
  sic_name?: string | null

  /**
   * Größenklasse — „Mega Cap" bis „Nano Cap".
   *
   * Sie ist über Börsen und Währungen hinweg vergleichbar, der Betrag
   * daneben nicht. Englisch, weil die Bezeichnungen so gebräuchlich sind.
   */
  market_cap_class?: string | null
  /**
   * Marktkapitalisierung, als ZEICHENKETTE — im Backend ein Decimal.
   *
   * Nur zusammen mit `market_cap_ccy` aussagekräftig: Samsungs 1.783 Bio.
   * Won sind weniger als Apples 3,9 Bio. Dollar. Die Datenbank lässt das
   * eine ohne das andere nicht zu.
   */
  market_cap?: string | null
  market_cap_ccy?: string | null
  /**
   * Derselbe Betrag in Dollar — UMGERECHNET, nicht gemessen.
   *
   * Danach wird sortiert: Über 23 Währungen hinweg ist der Originalbetrag
   * keine Reihenfolge, sondern nur eine Reihe verschieden großer Zahlen.
   */
  market_cap_usd?: string | null
  /** Tag, an dem der Betrag galt. Eine Marktkapitalisierung altert täglich. */
  market_cap_on?: string | null
}

/**
 * Ein Eintrag im Branchenbaum.
 *
 * `scheme` und `code` sind erst ZUSAMMEN eindeutig: „10" ist bei GICS der
 * Energiesektor und bei SIC nichts. Der Filter setzt aus beiden seinen Wert
 * `GICS:10` zusammen — nur so weiß das Backend, welche der vier Codespalten
 * gemeint ist.
 */
export interface Industry {
  scheme: string
  code: string
  name: string
  /** 1 ist die gröbste Ebene. */
  level: number
  /** Leer bei den Einträgen ganz oben. */
  parent_code?: string | null

  /**
   * Instrumente, die DIREKT auf diesem Code liegen.
   *
   * Nur beim Baum gefüllt, sonst `null` — die einfache Liste zählt nicht, weil
   * sie den Branchenfilter speist und dabei 78 ms für Zahlen zahlte, die
   * niemand ansieht.
   *
   * `null` heißt „nicht gezählt" und nicht „keine". Bei einer Zwischenebene ist
   * die Zahl meist 0, weil eingeordnet wird auf die feinste Stufe — ist sie es
   * NICHT, hängen Titel an einer Gruppe statt an einer Branche.
   */
  instrument_count?: number | null
  /** Dasselbe plus alles, was darunter hängt. Das ist die angezeigte Zahl. */
  total_count?: number | null
}

/** Der Baum einer Norm, flach, mit Zahlen. */
export interface IndustryOverview {
  scheme: string
  /** Eltern stehen VOR ihren Kindern — die Reihenfolge ist bedeutsam. */
  items: Industry[]
  /**
   * Instrumente ohne Code in dieser Norm — 18.075 von 67.263 bei GICS,
   * überwiegend ETFs. Kein Knoten des Baums: „nicht eingeordnet" ist keine
   * Branche. Aber ohne die Zahl passte die Summe der Branchen nicht zum
   * Bestand, und niemand könnte sagen, warum.
   */
  unclassified: number
  /**
   * Dieselben, aufgeschlüsselt nach Art: `S` Aktie, `E` ETF, `''` ohne Art.
   *
   * Der Unterschied ist keine Feinheit. Bei GICS sind es 16.244 ETFs und 1.831
   * Aktien — die ETFs sind **richtig** nicht eingeordnet, ein Fonds hat keinen
   * eigenen Geschäftszweck. Die Aktien sind eine **Lücke**. Als eine Zahl von
   * 18.075 ließe sich das eine nicht vom anderen unterscheiden.
   */
  unclassified_by_type: Record<string, number>
  /** Alle, die einen Code tragen. */
  classified: number
}

/** Ein Marktereignis — Termin, Ziel, Ausgang. Rohwerte, wie gespeichert. */
/** Eine Einschätzung — die Ampel zu einem Ziel, zu einem Zeitpunkt. */
export interface Assessment {
  id: number
  /** GREEN, AMBER oder RED. */
  rating: 'GREEN' | 'AMBER' | 'RED'
  /** Die Einschätzung in einer Zeile. */
  headline: string
  note: string | null
  /**
   * Worauf sie zielt — Schlüssel, Art und Name in EINEM Eintrag.
   * LEER heißt: allgemeine Markteinschätzung, an kein Papier gebunden.
   */
  targets: Ref[]
  /** Wann sie GALT — nicht, wann sie eingetragen wurde. */
  assessed_at: string
  /**
   * Wann zuletzt geprüft und bestätigt wurde, ohne dass sich etwas änderte.
   *
   * Leer heißt: seit dem Eintrag nicht mehr angesehen. Das ist eine Aussage —
   * eine grüne Ampel von vor drei Wochen ist etwas anderes als eine, die vor
   * einer Stunde bestätigt wurde.
   */
  confirmed_at: string | null
  source: string
  created: string
  updated: string | null
}

export interface AssessmentQuery {
  /**
   * Nur zu diesem Verweis — MIT Präfix: `isin:US0378331005`.
   *
   * `mkt:IW:GLOBAL` ist die allgemeine Marktlage. Dafür gab es einmal einen
   * eigenen Schalter `market_only`; seit die Gesamtlage einen Verweis trägt
   * wie jedes andere Ziel, findet dieselbe Angabe sie mit.
   */
  ref?: string
  rating?: string[]
  /** Ab diesem Zeitpunkt, einschliesslich. */
  since?: string
  /** Bis zu diesem Zeitpunkt, einschliesslich. */
  until?: string
  sort?: 'assessed_at' | 'rating' | 'created'
  dir?: 'asc' | 'desc'
  page?: number
  size?: number
  total?: boolean
}

/** Ein Referenzkurs der EZB — ein Wert je Bankarbeitstag. */
/**
 * Eine Rohlieferung, wie die Übersicht sie zeigt.
 *
 * OHNE Nutzlast — die kommt bewusst nicht mit: Eine Seite mit fünfzig
 * Lieferungen zöge sonst jede einzelne mit, und die FX-Erstbefüllung allein
 * wiegt 1,4 MB. Was die Liste braucht, ist `payload_bytes`.
 */
/** Eine Schlagzeile. STEHT IN KEINER TABELLE — sie ist flüchtig. */
export interface RssEntry {
  source_id: number
  /** Der fachliche Schlüssel der Quelle — `ecb-press`. */
  source_key: string
  source_name: string
  title: string
  description: string
  /** `null`, wenn der Feed kein pubDate mitschickt. */
  published_at: string | null
  link: string | null
}

export interface RssFeed {
  entries: RssEntry[]
  /**
   * Quellenname → Grund. Wer fragt, soll ERFAHREN, dass eine Quelle nicht
   * geliefert hat — sonst hält er eine unvollständige Liste für vollständig.
   */
  problems: Record<string, string>
  /** Welche Quellen aus dem Speicher kamen statt frisch geholt. */
  from_cache: string[]
  /**
   * Von wann der Stand ist: der älteste Abruf unter den gelieferten Quellen.
   * `null`, wenn keine geliefert hat.
   */
  fetched_at: string | null
}

export interface RssSource {
  id: number
  /** Der fachliche Schlüssel. Er, nicht die id, ist stabil. */
  key: string
  name: string
  url: string
  category: string | null
  /** 1 ist die wichtigste, 5 die unwichtigste. */
  priority: number
  active: boolean
}

export interface RssQuery {
  /** Als SCHLÜSSEL, nicht als Zahl — die wechselt. */
  sources?: string[]
  max_priority?: number
  since?: string
  limit_per_source?: number
  /**
   * Frisch holen, auch wenn der letzte Abruf noch gilt. Der Server schont
   * eine Quelle trotzdem eine Minute lang — `from_cache` sagt, welche.
   */
  refresh?: boolean
}

export interface ImportRecord {
  id: number
  content_type: string
  data_type: string
  source: string
  reference: string | null
  /** Umfang der Nutzlast in Bytes. */
  payload_bytes: number
  /** Wann übernommen. Leer heißt offen. */
  derived_at: string | null
  /** Woran die Ableitung scheiterte. */
  derive_error: string | null
  /** Ab wann gelöscht werden darf. Leer heißt unbefristet. */
  delete_at: string | null
  created: string
  updated: string | null
}

/** Dieselbe Lieferung samt dem ANFANG ihrer Nutzlast. */
export interface ImportDetail extends ImportRecord {
  payload_head: string | null
  /** Ob abgeschnitten wurde — sonst hielte jemand den Anfang für das Ganze. */
  payload_truncated: boolean
}

export interface ImportQuery {
  data_type?: string
  source?: string
  von?: string
  bis?: string
  sort?: 'created' | 'data_type' | 'source' | 'id'
  dir?: 'asc' | 'desc'
  page?: number
  size?: number
  total?: boolean
}

/**
 * Eine Verbindung des MCP-Zugangs — aus der Zugriffsdatei oder selbst
 * registriert. OHNE Geheimnis: `has_secret` sagt nur, ob es eines gibt.
 */
export interface OAuthClientInfo {
  client_id: string
  /** Die Kennung aus der Datei, bei registrierten der Host der Rückleitadresse. */
  label: string
  /** `unknown`: Es gibt nur noch Token, die Verbindung selbst nicht mehr. */
  source: 'config' | 'registered' | 'unknown'
  auth_method: string
  has_secret: boolean
  redirect_uris: string[]
  /** Was die Richtlinie HEUTE zugesteht. */
  scopes: string[]
  /** Was bei der Registrierung festgehalten wurde — nur bei registrierten. */
  registered_scopes: string[]
  created: string | null
  tokens_total: number
  tokens_active: number
  subjects: string[]
  last_token_at: string | null
}

/** Ein Erneuerungstoken — der Hash bleibt in der Datenbank. */
export interface OAuthToken {
  id: number
  subject: string
  client_id: string
  client_label: string
  scopes: string[]
  status: 'ACTIVE' | 'REVOKED' | 'EXPIRED'
  created: string
  expires_at: string
  revoked_at: string | null
}

export interface OAuthTokenQuery {
  subject?: string
  client_id?: string
  status?: string
  sort?: 'created' | 'expires_at' | 'subject' | 'id'
  dir?: 'asc' | 'desc'
  page?: number
  size?: number
  total?: boolean
}

export interface FxRate {
  id: number
  /** Von welcher Währung. Bei der EZB immer EUR. */
  base: string
  quote: string
  /** `EUR/USD` — fertig zusammengesetzt vom Dienst. */
  pair: string
  /** Der Tag der FESTSTELLUNG, nicht des Abrufs. */
  rate_date: string
  /** Fremdwährung je einen Euro: 1,1664 heißt 1 EUR = 1,1664 USD. */
  rate: string
  /**
   * Der Kurs des vorigen Bankarbeitstags.
   *
   * Kommt aus der Datenbank und wird VOR dem Zeitraumfilter gerechnet — sonst
   * hätte die erste Zeile jedes Ausschnitts keinen Vorgänger. `null` hat
   * genau eine Zeile: die älteste des Bestands.
   */
  previous_rate: string | null
  /** Veränderung in Prozent. `null`, wo es keinen Vorgänger gibt. */
  change: string | null
  source: string
  created: string
  updated: string | null
}

export interface FxRateQuery {
  base?: string
  quote?: string
  von?: string
  bis?: string
  sort?: 'rate_date' | 'rate' | 'created'
  dir?: 'asc' | 'desc'
  page?: number
  size?: number
  total?: boolean
}

export interface MarketEvent {
  id: number
  /** Speicherwert wie EARNINGS — die deutsche Übersetzung macht die Seite. */
  event_type: string
  /** Nur bei OTHER gefüllt — dort Pflicht. */
  other_label: string | null
  title: string
  /** Gruppiert Wiederkehrendes: US_CPI, NVDA_EARNINGS. */
  series: string | null
  /**
   * Worauf das Ereignis zielt — Schlüssel, Art und Name in EINEM Eintrag.
   *
   * LEER heißt marktweit — ein Verfallstag gehört zu keinem einzelnen Ziel.
   * MEHRERE sind erlaubt.
   */
  targets: Ref[]
  date_from: string | null
  date_to: string | null
  /** EXACT | DATE_RANGE | MONTH | QUARTER | ESTIMATED | UNKNOWN */
  date_precision: string
  status: string
  /** Der Termin VOR der letzten Verschiebung — die Verschiebung ist das Signal. */
  rescheduled_from: string | null
  /** Als Zeichenkette: Decimal-Werte, `number` verlöre Stellen. */
  previous: string | null
  forecast: string | null
  actual: string | null
  unit: string | null
  /** Das Herkunftssystem — MCP, YAHOO. Kein Beleg. */
  source: string
  sources: Source[]
  /**
   * Wann das Ereignis EINGETRAGEN wurde — nicht, wann es stattfindet.
   *
   * Ein Termin für nächstes Jahr kann heute angelegt worden sein.
   */
  created: string
}

export interface MarketEventQuery {
  /** Suchbegriff für Titel, Bezeichnung (bei Sonstigem) oder Serie. */
  q?: string
  event_type?: string[]
  status?: string[]
  /**
   * Termine ab diesem Tag, einschließend. Als TAG und nicht als Zeitpunkt:
   * Ein Ereignis hat einen Termin, keine Uhrzeit.
   *
   * Der Filter prüft auf ÜBERSCHNEIDUNG, nicht auf Enthaltensein — ein
   * mehrtägiges Ereignis wie Jackson Hole (27.–29.) wird auch gefunden, wenn
   * man den 28. sucht.
   */
  /**
   * Nur Ereignisse zu diesem Verweis — MIT Präfix, so wie er gespeichert ist:
   * `isin:US0378331005`, `comm:BRENT`, `ind:IW:10`.
   *
   * Ohne den Typ wäre er mehrdeutig: Ein Rohstoff und eine Branche dürfen
   * dasselbe Kürzel tragen.
   */
  ref?: string
  von?: string
  /** Termine bis zu diesem Tag, einschließend. */
  bis?: string
  sort?: 'date_from' | 'event_type' | 'title' | 'series' | 'status' | 'source'
      | 'created'
  dir?: 'asc' | 'desc'
  page?: number
  size?: number
  total?: boolean
}

/** Ein Rohstoff, wie die Nachschlagetabelle ihn zeigt. */
export interface Commodity {
  id: number
  /**
   * Selbst vergeben: BRENT, WTI, GOLD. Für Rohstoffe gibt es keine
   * börsenübergreifende Kennung wie die ISIN — CME und ICE haben eigene
   * Ticker, Bloomberg und Reuters je weitere, aber nichts Neutrales darüber.
   */
  code: string
  name: string
  /** ENERGY, METAL oder AGRI. Keine amtliche Taxonomie, sondern unsere. */
  category: string
  /**
   * Die Menge, auf die sich ein Preis bezieht: Barrel, Feinunze, Bushel.
   * Gehört zum Ticker, nicht zum Rohstoff: Kupfer notiert an der COMEX je
   * Pfund und an der LME je Tonne.
   */
  unit: string
  currency: string
  /**
   * Symbole mit Präfix — zwei Sorten mit unterschiedlicher Bedeutung: der
   * MIC der Börse (`IFEU:B`) und der Datenanbieter, unter dessen eigenem
   * Symbol wir den Kurs holen (`YAHOO:BZ=F`). Das zweite ist kein
   * Börsensymbol.
   */
  tickers: string[]
}

export interface CommodityQuery {
  /** Mehrere möglich — wird zu ?category=ENERGY&category=METAL. */
  category?: string[]
  sort?: 'code' | 'name' | 'category' | 'unit' | 'currency'
  dir?: 'asc' | 'desc'
  page?: number
  size?: number
  total?: boolean
}

/** Ein Vokabeleintrag, wie die Nachschlagetabelle ihn zeigt. */
export interface VocabularyEntry {
  id: number
  /** Der Raum: geo, crypt, mkt. Zugleich das Präfix des Verweises. */
  namespace: string
  /**
   * Die Norm, aus der der Code stammt — NICHT die Art des Dings. Deshalb
   * steht die Europäische Union unter ISO3166 (die Norm führt sie, neben EZ
   * und UN) und die NATO unter IW (die Norm führt sie nicht).
   */
  scheme: string
  /** Der Code innerhalb seiner Norm: DE, 150, NATO, BTC. */
  code: string
  name: string
  /**
   * Der übergeordnete Code innerhalb derselben Norm, oder null. Getragen
   * wird damit nur die M49-Schachtelung — Westeuropa unter Europa unter
   * Welt. Bündnisse bleiben leer: Ein Land gehört zu mehreren zugleich.
   */
  parent_code: string | null
  /**
   * Raum, Norm und Code zusammengesetzt — der Wert, wie er in `refs` steht.
   * KOMMT VOM SERVER und wird hier nicht gebaut: Sonst müsste die
   * Zusammensetzungsregel an zwei Stellen stehen.
   */
  ref: string
}

export interface VocabularyQuery {
  /** Sucht in Name UND Code — welches von beiden, muss niemand entscheiden. */
  q?: string
  /** Mehrere möglich — wird zu ?namespace=geo&namespace=mkt. */
  namespace?: string[]
  scheme?: string[]
  sort?: 'namespace' | 'scheme' | 'code' | 'name'
  dir?: 'asc' | 'desc'
  page?: number
  size?: number
  total?: boolean
}

/** Ein vorhandenes Raum-Norm-Paar, für die Auswahlfelder. */
export interface VocabularyNamespace {
  namespace: string
  scheme: string
}

/** Ein Handelsplatz, wie die Nachschlagetabelle ihn zeigt. */
export interface Exchange {
  id: number
  /** Der MIC nach ISO 10383 — hierüber sind die Instrumente verknüpft. */
  code: string
  /** Das gebräuchliche Kürzel. Darf fehlen und ist nicht eindeutig. */
  alias?: string | null
  name: string
  currency: string
  /** Zeitzonenname nach IANA. Die Zeiten darunter sind Ortszeit. */
  timezone: string
  open_time: string
  close_time: string
  /** Handelsplatz, der uns interessiert — von Hand gepflegt. */
  favorite: boolean
}

export interface ExchangeQuery {
  q?: string
  /** Mehrere möglich — wird zu ?currency=EUR&currency=USD. */
  currency?: string[]
  /** Die gebräuchlichen Kürzel, mehrere möglich. */
  alias?: string[]
  /** Weggelassen heißt "beide" — nicht "nur die übrigen". */
  favorite?: boolean
  sort?: 'code' | 'alias' | 'name' | 'currency' | 'timezone'
      | 'open_time' | 'close_time' | 'favorite'
  dir?: 'asc' | 'desc'
  page?: number
  size?: number
  total?: boolean
}

/** Eine Seite, wie sie jeder Listenendpunkt liefert. */
export interface PageOf<T> {
  items: T[]
  page: number
  size: number
  /** `null` heißt "nicht gezählt" — nicht "null Treffer". */
  total: number | null
  has_more: boolean
}

/** Ein Handelsplatz mit seinem Symbol. */
export interface TradingVenue {
  exchange: string
  /** Fehlt, wenn es zum Handelsplatz kein Symbol gibt — eine Lücke in den Daten. */
  symbol: string | null
  is_primary: boolean
}

/** Ein Instrument mit allem, was die Tabelle hergibt. */
/** Ein Stand der Geschäftszahlen — Rohwerte, nicht aufbereitet. */
export interface Fundamental {
  /** Tag des ABRUFS, nicht des Berichts. */
  fetched_on: string
  /** Die BILANZwährung — nicht zwingend die der Notierung. */
  currency: string
  /** YAHOO, TRADINGVIEW, SEC. */
  source: string
  /**
   * Die vier Beträge tragen ihre Bilanzwährung und ihre Umrechnung.
   * `null` heißt "nicht geliefert" — etwas anderes als 0.
   */
  market_cap: Money | null
  revenue_ttm: Money | null
  /** Anteil, nicht Prozent: "0.094" sind 9,4 %. KEIN Geldbetrag. */
  revenue_growth_ttm: string | null
  net_income_ttm: Money | null
  free_cash_flow_ttm: Money | null
  /** Ebenfalls ein Anteil. */
  fcf_margin_ttm: string | null
}

export interface InstrumentDetail {
  id: number
  isin: string
  name: string
  /** Die ersten zwei Zeichen der ISIN — abgeleitet, nicht gespeichert. */
  country_code: string
  /** S Aktie, E ETF, I Index. Entscheidet, ob Kennzahlen Sinn ergeben. */
  instrument_type: string | null
  primary_exchange: string
  venues: TradingVenue[]
  /** GICS-Sektor, Code und Klartext — leer bei Titeln ohne Einordnung. */
  sector_code: string | null
  sector_name: string | null
  /** Die jüngsten Kennzahlenstände, neuester zuerst. Höchstens acht. */
  fundamentals: Fundamental[]
  /** Größenklasse und Betrag. Getrennte Quellen — jedes darf einzeln fehlen. */
  market_cap_class: string | null
  market_cap: string | null
  market_cap_ccy: string | null
  market_cap_usd: string | null
  market_cap_on: string | null
  created: string
  /** `null` heißt "noch nie geändert" — kein fehlender Wert. */
  updated: string | null
}

/** Ein Watchlist-Eintrag samt dem Instrument, auf das er zeigt. */
export interface WatchlistItem {
  id: number
  /** Kennung des INSTRUMENTS — die Zeile führt auf dessen Detailansicht. */
  instrument_id: number
  isin: string
  name: string
  /** Der GICS-Sektor im Klartext. Fehlt bei ETFs: Ein Fonds hat keinen
   *  eigenen Geschäftszweck, er hält Titel, die einen haben. */
  sector_name?: string | null
  /**
   * Der aktuelle Kurs am Hauptplatz.
   *
   * `null` heißt NICHT „kein Kurs vorhanden", sondern „kein AKTUELLER":
   * Ein veralteter wird unterdrückt, weil er sonst eine Aktualität
   * behauptete, die es nicht gibt. Die Tabelle zeigt dann einen Strich.
   */
  quote?: Money | null
  /** Wann er beobachtet wurde — die Schwelle lässt mehrere Tage zu, also
   *  gehört der Zeitpunkt dazu. */
  quote_at?: string | null
  primary_exchange: string
  reasons: string[]
  /** `true` unter Beobachtung, `false` ruhend — nicht abgeschlossen. */
  active: boolean
  created: string
  updated: string | null
}

/**
 * Der geänderte Eintrag nach dem Schreiben — ohne die Stammdaten.
 *
 * Die Oberfläche lädt danach neu: Ein aufgenommener Titel steht je nach
 * Sortierung an anderer Stelle, ein ruhender fällt aus der Arbeitsliste.
 */
export interface WatchlistEntry {
  id: number
  instrument_id: number
  reasons: string[]
  active: boolean
}

export interface WatchlistQuery {
  q?: string
  /** Zielwährungen für den Kurs. Als Liste, obwohl die Auswahl eine zulässt. */
  currency?: string[]
  status?: 'active' | 'resting' | 'all'
  reason?: string
  sort?: 'name' | 'isin' | 'created' | 'active'
  dir?: 'asc' | 'desc'
  page?: number
  size?: number
  total?: boolean
}

/** Ein Kurs samt Instrument und Handelsplatz. */
export interface Quote {
  id: number
  instrument_id: number
  isin: string
  name: string
  exchange: string
  observed_at: string
  price: Money
}

/**
 * Ein Betrag in einer Umrechnung — Währung, Wert, und womit gerechnet wurde.
 *
 * `rate` ist der EFFEKTIVE Kurs: "USD→EUR zu 0,857339" lässt sich nachrechnen,
 * der Tabellenwert 1,1664 müsste erst umgedreht werden.
 */
export interface ConvertedAmount {
  currency: string
  amount: string
  rate: string
  rate_date: string
}

/**
 * Ein Geldbetrag samt Währung — und, wenn `?currency=` gesetzt war, seinen
 * Umrechnungen.
 *
 * `currencies` ist LEER statt `null`, wenn nichts umzurechnen war: Die Anzeige
 * läuft über eine leere Liste und tut nichts, statt einen Sonderfall zu
 * brauchen. Eine Währung, für die kein Kurs vorliegt, erscheint gar nicht.
 *
 * Beträge sind Zeichenketten: Als Zahl würde aus 106,10 die 106.09999….
 */
export interface Money {
  amount: string
  currency: string
  currencies: ConvertedAmount[]
}

/** Ein Handelstag samt Instrument und Handelsplatz. */
export interface PriceHistory {
  id: number
  instrument_id: number
  isin: string
  name: string
  exchange: string
  trading_date: string
  open: Money
  high: Money
  low: Money
  close: Money
  /** Um Dividenden und Splits bereinigt — für Renditevergleiche. */
  adj_close: Money
  volume: number
}

export interface PriceQuery {
  /** Zielwährungen für die Umrechnung. Der Originalbetrag kommt immer mit. */
  currency?: string[]
  q?: string
  exchange?: string
  /**
   * Nur Kurse dieser Instrumente.
   *
   * NEBEN `q` und nicht darin: `q` durchsucht Name und Ticker als
   * Teilzeichenfolge, die ISIN steht in beidem nicht. Ueber `q` waere sie gar
   * nicht zu finden.
   */
  isin?: string[]
  since?: string
  until?: string
  sort?: string
  dir?: 'asc' | 'desc'
  page?: number
  size?: number
  total?: boolean
}

/** Ein Beleg: woher eine Meldung, ein Ereignis oder eine Idee ihre Angaben hat. */
export interface Source {
  id: number
  title: string
  url: string
  publisher: string | null
}

export interface NewsItem {
  id: number
  headline: string
  body: string
  /** Der Link der Meldung selbst — kein Beleg. */
  source_url: string
  /** Die Belege dahinter. */
  sources: Source[]
  /** Sieben Stufen von -3 bis +3. NICHT mitteln. */
  sentiment: number
  published_at: string
  /**
   * Wann die Meldung ERFASST wurde — nicht, wann sie erschien.
   *
   * Eine Meldung von gestern kann heute eingelesen worden sein;
   * die Liste zeigt beide Zeitpunkte in getrennten Spalten.
   */
  created: string
  /**
   * Worauf die Meldung zeigt — Titel, Rohstoffe, Branchen, Länder,
   * Bündnisse, Marktsegmente, Kryptowährungen.
   *
   * EINE Liste für alle Arten. Hier standen fünf Felder nebeneinander, und
   * die Ansicht führte sie beim Anzeigen wieder zusammen.
   *
   * LEER heißt: ohne Bezug. Eine Zinsentscheidung gehört zu keinem
   * einzelnen Titel — das ist eine Aussage, kein Mangel.
   */
  targets: Ref[]
}

export interface NewsQuery {
  q?: string
  // Alle drei als LISTE und ODER-verknüpft, auch über die Arten hinweg:
  // Wer nach einem Papier und einem Rohstoff zugleich fragt, will beide
  // Listen — eine Meldung ist nie beides zugleich.
  isin?: string[]
  commodity?: string[]
  // Je Eintrag NORM:CODE — "IW:10". Die Norm gehört dazu, "10" allein ist
  // bei GICS die Energie und bei IW die Halbleiter.
  industry?: string[]
  /**
   * Laender, Regionen, Buendnisse, Marktsegmente, Kryptowaehrungen — je
   * Eintrag der VOLLE Verweis: `geo:ISO3166:DE`, `mkt:IW:EQUITY`.
   */
  vocabulary?: string[]
  /**
   * Der fertige Verweis, ALLE Arten — `isin:US0378331005`, `comm:BRENT`,
   * `ind:IW:10`, `geo:ISO3166:DE`.
   *
   * NEBEN den vier Feldern darueber: Die Filterleiste bietet je Art ein Feld
   * mit dem Kuerzel ohne Praefix. Wer dagegen einen fertigen Verweis hat —
   * das Popover etwa —, gibt ihn hier hin, statt ihn zu zerlegen. Beide Wege
   * landen ODER-verknuepft in derselben Liste.
   */
  ref?: string[]
  sentiment?: 'all' | 'positive' | 'neutral' | 'negative'
  since?: string
  until?: string
  sort?: 'published_at' | 'sentiment' | 'created'
  dir?: 'asc' | 'desc'
  page?: number
  size?: number
  total?: boolean
}

export interface InstrumentQuery {
  q?: string
  /** Mehrere MIC möglich — wird zu ?exchange=XNAS&exchange=XETR. */
  exchange?: string[]
  /** 'S' Aktie, 'E' ETF, 'I' Index — die Buchstaben der Datenbank. */
  instrument_type?: string[]
  /**
   * Branchen, je Eintrag `NORM:CODE` — etwa `GICS:10`.
   *
   * Die Norm steht mit im Wert, weil sie bestimmt, welche Spalte gefiltert
   * wird. Damit greift EIN Feld auf alle vier Codespalten, ohne dass die
   * Oberfläche die Zuordnung kennen muss.
   */
  industry?: string[]
  sort?: 'name' | 'isin' | 'instrument_type' | 'primary_exchange'
      | 'sector_name' | 'sic_name' | 'market_cap_class' | 'market_cap_usd'
  dir?: 'asc' | 'desc'
  page?: number
  size?: number
  total?: boolean
}

export interface IndustryQuery {
  /** Nur diese Normen, etwa GICS oder SIC. */
  scheme?: string[]
  /** Nur diese Ebene; 1 ist die gröbste. */
  level?: number
  sort?: 'scheme' | 'code' | 'name' | 'level'
  dir?: 'asc' | 'desc'
  page?: number
  size?: number
  total?: boolean
}

/**
 * Baut die Abfrage. Leere Angaben fallen weg.
 *
 * Ein `q=` oder `exchange=` mitzuschicken ist nicht dasselbe wie es
 * weglassen — als Filter genommen träfe eine leere Zeichenkette keinen
 * Handelsplatz, und die Liste wäre ohne erkennbaren Grund leer.
 */
function query(params: object): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    // Eine Liste wird zu WIEDERHOLTEN Parametern: ?exchange=XNAS&exchange=XETR.
    // `set` mit einem Array ergäbe daraus "XNAS,XETR" — eine einzige
    // Zeichenkette, die das Backend als einen Wert läse. Eine leere Liste
    // entfällt ganz; sie bedeutet "kein Filter", nicht "keiner von diesen".
    if (Array.isArray(value)) {
      for (const einzel of value) {
        if (einzel === undefined || einzel === null || einzel === '') continue
        search.append(key, String(einzel))
      }
      continue
    }
    search.set(key, String(value))
  }
  const text = search.toString()
  return text ? `?${text}` : ''
}

// ------------------------------------------------------------- Recherche
//
// Laufende Vorgaenge zu Titeln — Verfahren, Genehmigungen, Uebernahmen.
// NUR LESEND: Faelle werden recherchiert und ueber den MCP-Zugang eingetragen,
// nicht in einem Formular erfasst. Es gibt hier deshalb kein Gegenstueck zum
// Schreiben.

/** Ein betroffener Titel, mit seiner Rolle im Vorgang. */
export interface CaseInstrument {
  id: number
  isin: string
  name: string
  /** PRIMARY, AFFECTED, ACQUIRER, TARGET oder PEER. */
  role: string
  note: string | null
}

/** Ein Fall in der Liste. */
export interface ResearchCase {
  id: number
  reference: string | null
  title: string
  case_type: string
  status: string
  stage: string | null
  opened_at: string | null
  /**
   * Letzte wesentliche Entwicklung. Fehlt sie, gilt `updated` — sonst stünde
   * bei jedem frisch angelegten Fall ein Strich, und die Sortierung nach
   * „zuletzt bewegt" wäre für neue Fälle blind.
   */
  last_material_update_at: string | null
  updated: string | null
  /** 0 ohne Belang bis 4 Kurskorridor unzuverlässig. */
  regime_impact: number
  trading_impact: string
  overhang_score: number | null
  /** In Prozent, als Zeichenkette — Decimal auf dem Server. */
  probability: string | null
  active: boolean
  instruments: CaseInstrument[]
  /**
   * Wann der Fall ANGELEGT wurde — nicht, wann der Vorgang begann.
   *
   * Ein Verfahren von 2019 kann gestern erfasst worden sein;
   * `opened_at` sagt das eine, dieses Feld das andere.
   */
  created: string
}

export interface CaseUpdate {
  id: number
  event_at: string | null
  /** Wann WIR es erfuhren. Für eine Rückschau der maßgebliche Zeitpunkt. */
  recorded_at: string
  headline: string
  fact_summary: string | null
  analyst_comment: string | null
  update_type: string
  /** POSITIVE, NEUTRAL, NEGATIVE oder UNCLEAR. */
  direction: string | null
  materiality: number | null
  probability_before: string | null
  probability_after: string | null
}

export interface CaseSource {
  id: number
  source_type: string
  publisher: string | null
  title: string | null
  url: string | null
  published_at: string | null
  primary_source: boolean
  official: boolean
  excerpt: string | null
}

export interface CaseCatalyst {
  id: number
  catalyst_type: string
  title: string
  date_from: string | null
  date_to: string | null
  /** EXACT, DATE_RANGE, MONTH, QUARTER, ESTIMATED oder UNKNOWN. */
  date_precision: string
  confirmed: boolean
  expectation_basis: string | null
  expected_materiality: number | null
  status: string
}

export interface CaseAction {
  id: number
  action_type: string
  description: string | null
  /** DATE, EVENT oder RECURRING. */
  trigger_type: string
  due_at: string | null
  trigger_event: string | null
  frequency: string | null
  /** 1 hoch, 2 normal, 3 niedrig. */
  priority: number
  status: string
}

export interface CaseDetail extends ResearchCase {
  jurisdiction: string | null
  authority: string | null
  first_known_at: string | null
  last_verified_at: string | null
  next_review_at: string | null
  summary: string | null
  analyst_assessment: string | null
  exposure_low: Money | null
  exposure_base: Money | null
  exposure_high: Money | null
  /** Bleibt daneben stehen: sie kann feststehen, bevor ein Betrag es tut. */
  exposure_currency: string | null
  exposure_basis: string | null
  company_provision: Money | null
  confidence: number | null
  resolved_at: string | null
  outcome_summary: string | null
  outcome_vs_expectation: string | null
  lessons: string | null
  updates: CaseUpdate[]
  sources: CaseSource[]
  catalysts: CaseCatalyst[]
  actions: CaseAction[]
}

export interface DueAction extends CaseAction {
  case_id: number | null
  case_title: string | null
}

/**
 * Eine Bewegung in der fallübergreifenden Liste — ein Verlaufseintrag mit dem
 * Fall und dessen Titeln daneben, damit die Zeile ohne zweiten Aufruf lesbar ist.
 */
export interface CaseMovement extends CaseUpdate {
  case_id: number
  case_title: string
  case_type: string
  case_status: string
  regime_impact: number
  trading_impact: string
  overhang_score: number | null
  probability: string | null
  case_active: boolean
  instruments: CaseInstrument[]
}

export interface UpcomingCatalyst extends CaseCatalyst {
  case_id: number | null
  case_title: string | null
  instrument_id: number
  instrument_name: string
  instrument_isin: string
}

export interface ResearchSummary {
  open_cases: number
  high_impact: number
  due_actions: number
  upcoming_catalysts: number
}

export interface ResearchQuery {
  q?: string
  active_only?: boolean
  case_type?: string
  status?: string
  min_impact?: number
  isin?: string
  /** Nur Fälle, die sich seit diesem Zeitpunkt geändert haben — „was ist neu". */
  changed_since?: string
  /** Nur Fälle ohne wesentliche Entwicklung seit diesem Zeitpunkt — „was liegt still". */
  updated_before?: string
  sort?: 'opened_at' | 'title' | 'regime_impact' | 'overhang_score'
      | 'created'
       | 'probability' | 'next_review_at' | 'last_material_update_at'
  dir?: 'asc' | 'desc'
  page?: number
  size?: number
  total?: boolean
}

// ------------------------------------------------------------- Auswertungen

/**
 * Ein Einzelwert der Verfassung samt seiner Punktzahl.
 *
 * `points: null` heißt NICHT BEWERTBAR — die Daten fehlen. Das ist etwas
 * anderes als 0, was eine Wertung wäre.
 */
export interface Term {
  name: string
  /** KEIN Geldbetrag: alle vier Terms sind in ATR gerechnet oder Verhältnisse. */
  value: string | null
  points: number | null
  grade: string
}

/** Ein Preisband aus den Quantilen der Kurse — also Geld. */
export interface Corridor {
  lower: Money
  upper: Money
  middle: Money
}

/** Die Verfassung eines Titels — unabhängig vom heutigen Tag. */
export interface Condition {
  bars: number
  /** „So weit bewegt sich der Titel an einem normalen Tag" — eine Kursspanne. */
  atr_short: Money | null
  atr_long: Money | null
  corridor_micro: Corridor | null
  corridor_short: Corridor | null
  corridor_long: Corridor | null
  /**
   * Die drei Verschiebungen stehen in ATR, nicht in Währung: „das kurze
   * Preisband liegt 0,4 ATR über dem langen". Genau dafür wird geteilt —
   * damit sich zwei Titel vergleichen lassen, deren Kurse nichts miteinander
   * zu tun haben.
   */
  mid_shift: string | null
  lower_shift: string | null
  /** Nur zur Anzeige — geht NICHT in den Score. */
  micro_shift: string | null
  slope: string | null
  vol_ratio: string | null
  terms: Term[]
  score: number | null
}

/**
 * Ein Grund — für drei Leser zugleich.
 *
 * `code` ist maschinenlesbar und bleibt stabil, auch wenn der Satz
 * umformuliert wird; `short` passt in eine Tabellenspalte, `text` ist der
 * ganze Satz, `values` trägt die gemessenen Zahlen.
 */
export interface Reason {
  code: string
  /** `veto` verhindert den Trade, `caution` steht knapp an einer Schwelle, `support` spricht dafür. */
  kind: 'veto' | 'caution' | 'support'
  short: string
  text: string
  values: Record<string, unknown>
}

export interface Entry {
  target: Money | null
  stop: Money | null
  /** Ziel minus Einstieg — 1,76 sagt nichts darüber, ob es um vier Euro geht oder um vierzig. */
  chance: Money | null
  /** Einstieg minus Stop. */
  risk: Money | null
  /** Das Verhältnis der beiden. KEINE Währung: 1,76 ist 1,76. */
  risk_reward: string | null
  /** Warum kein Stop bestimmt wurde. */
  stop_missing: string | null
}

/** Ein einzelner Term des Boden-Scores. */
export interface DipTerm {
  name: string
  /**
   * Der Betrag, an dem bewertet wurde — getrennt vom erklärenden Text.
   *
   * Er stand einmal darin („253.3 Mrd. USD") und war damit nicht mehr
   * umzurechnen. Die Umrechnung ERSETZT ihn nicht: Bewertet wird an
   * Dollarschwellen, und der Eurowert passte nicht mehr zu der Schwelle,
   * gegen die gemessen wurde.
   */
  value: Money | null
  /** `null` heißt NICHT BEWERTBAR — die Daten fehlen. Nicht dasselbe wie 0. */
  points: number | null
  /** Was dieser Term bei DIESER Datenreife hätte beitragen können. */
  maximum: number
  reason: string
}

/** Einer der vier Blöcke, aus denen sich der Score zusammensetzt. */
export interface DipBlock {
  name: string
  points: number
  maximum: number
  terms: DipTerm[]
}

/** Die Bewertung eines Instruments im Buy-the-Dip-Screen. */
export interface DipAssessment {
  isin: string | null
  name: string | null
  /**
   * Die technische Kennung — für den Sprung auf die Stammdaten.
   *
   * NEBEN der ISIN, nicht statt ihrer: Die ISIN ist die fachliche Kennung,
   * die auch außerhalb gilt; diese hier gilt nur hier.
   */
  instrument_id: number | null
  exchange: string | null
  currency: string | null
  bars: number
  /** Gar nicht bewertet — zu klein oder ohne Fundamentaldaten. */
  excluded: boolean
  exclusion_reason: string
  score: number
  /** Was bei dieser Datenreife erreichbar war. 60 von 70 ≠ 60 von 100. */
  maximum: number
  status: string
  fundamental: DipBlock
  selloff: DipBlock
  bottoming: DipBlock
  liquidity: DipBlock
  penalty_points: number
  penalty_reasons: string[]
  /** Nicht prüfbar mangels Historie — KEIN Abzug. */
  penalty_unchecked: string[]
  rsi: string | null
  drawdown_pct: string | null
  perf_1m_pct: string | null
  volume_ratio: string | null
  as_of: string | null
  /**
   * Das Jahreshoch, gegen das `drawdown_pct` gemessen wurde — samt Tag.
   *
   * Als `Money`, obwohl der Score in Dollar rechnet: Bewertet wird an
   * Dollarschwellen, angezeigt der Kurs, den es an der Börse gab.
   */
  high_52w: Money | null
  high_52w_on: string | null
}

export interface DipReport {
  name: string
  trading_date: string | null
  rows: DipAssessment[]
  unknown: string[]
  /** Die Signalstufen, gegen die gemessen wurde. */
  thresholds: Record<string, number>
}

/** Die Bewertung eines Instruments. */
export interface Assessment {
  isin: string | null
  name: string | null
  /**
   * Die technische Kennung — für den Sprung auf die Stammdaten.
   *
   * NEBEN der ISIN, nicht statt ihrer: Die ISIN ist die fachliche Kennung,
   * die auch außerhalb gilt; diese hier gilt nur hier.
   */
  instrument_id: number | null
  exchange: string | null
  currency: string | null
  /** `true` = auf einem Kurs des laufenden Handelstags gerechnet. */
  live: boolean
  price: Money | null
  previous_close: Money | null
  day_low: Money | null
  condition: Condition
  /** Abstand zum Vortagesschluss IN ATR — kein Geldbetrag. */
  daily_stretch: string | null
  dip_score: number | null
  normal_price: Money | null
  /** Normalkurs minus Kurs: eine Kursdifferenz und damit Geld. */
  reversion_room: Money | null
  /** Derselbe Weg, in ATR gemessen. Kein Geld. */
  reversion_room_atr: string | null
  entry: Entry
  /** Die Vetos in Kurzform — abgeleitet aus `reasons`. Leer = handelbar. */
  vetos: string[]
  /** Alle Gründe: erst was verhindert, dann was dafür spricht, dann Vorbehalte. */
  reasons: Reason[]
  /** Das Fazit in einem Absatz — "X, weil Y und Z". */
  summary: string
  /** `null`, sobald ein Veto steht. */
  entry_score: number | null
  status: string
}

/** Eine Stufe samt dem, was sie für eine Entscheidung bedeutet. */
export interface Level {
  /**
   * Untergrenze beim Status, Obergrenze bei der Verfassung.
   * `null` heißt: entsteht nicht aus einem Punktwert — NO TRADE kommt aus
   * dem Veto.
   */
  bound: number | null
  label: string
  meaning: string
}

/**
 * Womit die Werte zu vergleichen sind.
 *
 * Kommt vom Server mit, statt hier verdrahtet zu sein: Die Schwellen stehen
 * in `config/reports/` und ändern sich, sobald jemand die Strategie
 * nachjustiert. Fest hinterlegt liefen Anzeige und Rechnung auseinander.
 */
export interface Legend {
  status: Level[]
  condition: Level[]
  thresholds: Record<string, string>
}

export interface Report {
  name: string
  rows: Assessment[]
  /** ISINs ohne Instrument — bewertet werden kann nur, was bekannt ist. */
  unknown: string[]
  trading_date: string | null
  legend: Legend
}

// ------------------------------------------------------------ Handelsideen
//
// Vorschläge eines Analysten samt ihren Schwellen. NUR LESEND: Eingetragen
// werden sie über den MCP-Zugang — dieselbe Trennung wie bei Meldungen und
// Fällen.

/** Eine Bedingung, unter der eine Tranche greift. */
export interface Trigger {
  /** LIMIT Spanne · BREAKOUT Ausbruch · CLOSE_ABOVE Schluss darüber · RETEST Rücktest · RECLAIM Rückeroberung */
  trigger_type: 'LIMIT' | 'BREAKOUT' | 'CLOSE_ABOVE' | 'RETEST' | 'RECLAIM'
  price: Money
  /** Nur bei LIMIT: die obere Kante. */
  price_high: Money | null
  /** Wie lange die Bedingung halten muss. */
  confirm_minutes: number | null
  max_spread: Money | null
}

/** Ein Schritt des gestaffelten Einstiegs. Die Auslöser sind ODER-verknüpft. */
export interface Tranche {
  sequence: number
  note: string | null
  triggers: Trigger[]
}

export interface IdeaTarget {
  sequence: number
  price: Money
  reached_at: string | null
}

/** Ein Eintrag der Fortschreibung — er trägt den Zustand DANACH. */
export interface IdeaUpdate {
  id: number
  event_at: string | null
  recorded_at: string
  headline: string
  note: string | null
  /** `null` heißt unverändert — nicht "auf leer gesetzt". */
  status_after: string | null
  classification_after: string | null
  entry_low_after: Money | null
  entry_high_after: Money | null
  chase_limit_after: Money | null
  invalid_below_after: Money | null
}

export interface TradeIdea {
  id: number
  instrument_id: number
  isin: string
  name: string
  exchange: string
  currency: string
  /**
   * Branche und Kapitalisierung des Titels, aus den Stammdaten — für die
   * Zeile unter dem Namen. Leer, wo die Stammdaten nichts hergeben.
   */
  sector_name?: string | null
  market_cap?: string | null
  market_cap_ccy?: string | null

  headline: string
  rationale: string | null
  classification: 'WATCH' | 'STAGED_BUY' | 'DIP_ONLY' | 'TRIGGER_ONLY'
                | 'BOTTOMING_REQUIRED'
  risk_profile: 'BALANCED' | 'AGGRESSIVE' | null
  /**
   * ENTERED — „im Markt" — ergibt sich aus den Transaktionen im Depot und
   * wird nie gesetzt. REJECTED — „abgelehnt" — setzt nur der Mensch.
   */
  status: 'PROPOSED' | 'ACTIVE' | 'ENTERED' | 'INVALIDATED' | 'EXPIRED'
        | 'CLOSED' | 'REJECTED'
  /** Ob die Idee noch gilt — abgeleitet aus dem Status. */
  open: boolean
  /** Was aus den Transaktionen folgt — gerechnet, nicht gespeichert. */
  position: Position

  /**
   * Der jüngste Tageskurs — was gerade gilt.
   *
   * NICHT dasselbe wie `reference_price`: Der ist der Stand, auf den sich der
   * Analyst beim Schreiben bezog, und ändert sich nie. Dieser bewegt sich —
   * erst beide zusammen sagen, ob die Idee noch am Kurs ist.
   */
  last_price: Money | null
  last_price_at: string | null

  reference_price: Money | null
  reference_at: string | null
  entry_low: Money | null
  entry_high: Money | null
  planned_average: Money | null
  /** Darüber nicht mehr hinterherlaufen. NICHT dasselbe wie die Ungültigkeit. */
  chase_limit: Money | null
  invalid_below: Money | null
  valid_until: string | null
  /** KEIN Geldbetrag: ein Verhältnis hat keine Währung. */
  risk_reward: string | null
  constraint_note: string | null
  /** Das erste Verkaufsziel — damit die Zeile die Chance rechnet, ohne das Detail. */
  first_target?: Money | null

  created: string
  updated: string | null
}

export interface TradeIdeaDetail extends TradeIdea {
  tranches: Tranche[]
  targets: IdeaTarget[]
  /** Neueste zuerst. */
  updates: IdeaUpdate[]
  /**
   * Die Transaktionen dieser Idee aus dem Depot, in der Reihenfolge des
   * Handels — älteste zuerst, so wie gerechnet wird.
   */
  transactions: Transaction[]
  /** Die offenen Orders dieser Idee. */
  open_orders: Order[]
  /** Woher die Einschätzung stammt. */
  sources: Source[]
}

/**
 * Was aus den Transaktionen folgt — Durchschnittseinstand, nicht FIFO.
 *
 * `open` ist die Zahl, die zählt: An einer Idee trägt sie den Status
 * ENTERED, und der Status lässt sich nicht setzen, solange sie wahr ist.
 */
export interface Position {
  quantity: string
  /** Leer ohne Stücke — ein Durchschnitt über nichts ist keine Zahl. */
  average_price: Money | null
  /**
   * Leer ohne Transaktionen — und leer, wenn die Position einer Idee Depots
   * mit verschiedenen Währungen zusammenfasst: Stücke addieren sich, Euro
   * und Dollar nicht.
   */
  invested: Money | null
  realized: Money | null
  /** Die Währung der Beträge, die des Depots. Leer wie oben. */
  currency: string | null
  open: boolean
  /** Käufe und Verkäufe zusammen. */
  trades: number
  first_at: string | null
  last_at: string | null
}

// ================================================================= Depot
//
// Drei Zeitformen: Die Order ist in Auftrag gegeben, die Transaktion ist
// geschehen, die Position folgt daraus. Die Kasse — Einlage, Kontostand,
// reserviert, frei — wird gerechnet und steht in keiner Tabelle.

export type OrderStatus = 'OPEN' | 'CANCELLED' | 'FILLED' | 'EXPIRED'

/** Der Kopf: Name, Währung, Einlage, Gebühr. */
export interface Depot {
  id: number
  /** Die Kennung am Schirm, eindeutig. */
  name: string
  currency: string
  /** Die Einlage — nicht der Kontostand, der steht in der Kasse. */
  cash: Money
  /** Was eine Ausführung kostet, in Depotwährung. */
  fee_per_order: Money
  created: string
  updated: string | null
}

export interface Kasse {
  cash: Money
  /** Einlage minus Käufe plus Verkäufe, zu den festgehaltenen Kursen. */
  balance: Money
  /** Was offene Kauforders binden: Stück mal Limit plus Gebühr. */
  reserved: Money
  /** Kontostand minus Reservierung. Bis hierher darf eine Kauforder gehen. */
  free: Money
}

/** Ein Depot in der Übersicht: Kopf, Kasse und zwei Zähler. */
export interface DepotSummary {
  id: number
  name: string
  currency: string
  cash: Money
  fee_per_order: Money
  kasse: Kasse
  open_orders: number
  holdings: number
}

/** Eine Limitorder samt Instrument, Idee und Depot. */
export interface Order {
  id: number
  depot_id: number
  depot_name: string
  idea_id: number
  headline: string
  instrument_id: number
  isin: string
  name: string
  exchange: string
  side: 'BUY' | 'SELL'
  /** KEIN Geldbetrag: eine Stückzahl hat keine Währung. */
  quantity: string
  /** In der Währung des Depots. */
  limit_price: Money
  /** Die Währung des Depots — jeder Betrag dieser Order steht darin. */
  currency: string
  valid_until: string
  status: OrderStatus
  /** Warum dieser Titel jetzt. */
  reason: string
  cancelled_at: string | null
  cancel_reason: string | null
  created: string
  updated: string | null
}

/** Was tatsächlich gehandelt wurde. Unveränderlich: löschen und neu eintragen. */
export interface Transaction {
  id: number
  depot_id: number
  depot_name: string
  side: 'BUY' | 'SELL'
  quantity: string
  /** In der Währung des Depots. */
  price: Money
  /** Die Währung des Depots — jeder Betrag dieser Transaktion steht darin. */
  currency: string
  fee: Money
  executed_at: string
  instrument_id: number
  isin: string
  name: string
  exchange: string
  /** Die Idee dahinter, wenn es eine gab. */
  idea_id: number | null
  headline: string | null
  /** Die Order dahinter. Dann wird sie an der Order zurückgenommen. */
  order_id: number | null
  /** Die Kassenbewegung: Kauf negativ, Verkauf positiv, jeweils nach Gebühr. */
  cash_delta: Money
  note: string | null
  created: string
}

/** Eine Position im Depot — über alle Transaktionen des Titels am Platz. */
export interface Holding {
  instrument_id: number
  isin: string
  name: string
  exchange: string
  /** Die Währung des Depots. */
  currency: string
  position: Position
  /** Die offene Idee zu diesem Titel, wenn es eine gibt. */
  idea_id: number | null
  headline: string | null
}

export interface DepotView {
  depot: Depot
  kasse: Kasse
  /** Die offenen Orders. */
  orders: Order[]
  holdings: Holding[]
  /** Der Kontoauszug, jüngste zuerst. */
  transactions: Transaction[]
}

export interface DepotInput {
  name: string
  cash: string
  fee_per_order: string
}

export interface OrderInput {
  idea_id: number
  side: 'BUY' | 'SELL'
  quantity: string
  limit_price: string
  valid_until: string
  reason: string
  tranche_sequence?: number | null
}

/** Eine Order als ausgeführt buchen. Ohne Stück gilt die der Order, ohne Gebühr die des Depots. */
export interface ExecuteInput {
  price: string
  executed_at: string
  quantity?: string | null
  fee?: string | null
  note?: string | null
}

/** Handel ohne Order — mit Idee oder mit ISIN und Handelsplatz. */
export interface TransactionInput {
  side: 'BUY' | 'SELL'
  quantity: string
  price: string
  executed_at: string
  fee?: string | null
  note?: string | null
  idea_id?: number | null
  isin?: string | null
  exchange?: string | null
  tranche_sequence?: number | null
}

/** Eine Entwicklung eintragen — vom Schirm aus vor allem das Abschließen. */
export interface IdeaUpdateInput {
  headline: string
  note?: string | null
  event_at?: string | null
  /** Nicht ENTERED — der ergibt sich aus den Transaktionen. */
  status_after?: 'PROPOSED' | 'ACTIVE' | 'INVALIDATED' | 'EXPIRED' | 'CLOSED' | 'REJECTED' | null
  invalid_below_after?: string | null
}

export interface TradeIdeaQuery {
  /** Zielwährungen für die Umrechnung. Der Originalbetrag kommt immer mit. */
  currency?: string[]
  q?: string
  isin?: string
  classification?: string
  status?: string
  open_only?: boolean
  /** Nur Ideen mit Stücken im Markt — dasselbe wie status=ENTERED. */
  in_position?: boolean
  /** Angelegt ODER fortgeschrieben seit diesem Zeitpunkt — die Frage des Feeds. */
  changed_since?: string
  /** Dieselben Werte, die der Endpunkt zulässt. */
  sort?: 'created' | 'updated' | 'headline' | 'classification' | 'status'
      | 'name' | 'exchange' | 'entry' | 'chase_limit' | 'invalid_below'
      | 'risk_reward' | 'risk_profile' | 'reference_price'
  dir?: 'asc' | 'desc'
  page?: number
  size?: number
  total?: boolean
}

export const api = {
  /**
   * Prüft die Zugangsdaten und sagt, wem sie gehören.
   *
   * Bei Basic Auth gibt es keine Sitzung, die anzulegen wäre — die Daten gehen
   * bei jedem Aufruf mit. Dieser Aufruf ist also die Anmeldung UND die
   * laufende Prüfung, ob sie noch gilt.
   */
  session: () => request<Session>('/session'),

  /**
   * Die Instrumentenliste.
   *
   * Das Signal geht bis an `fetch` durch. Es nur nach der Antwort zu pruefen
   * wuerde zwar verhindern, dass eine alte Antwort eine neue ueberschreibt —
   * die Anfragen liefen aber alle zu Ende und belasteten den Server ohne Not.
   */
  masterData: (params: InstrumentQuery = {}, signal?: AbortSignal) =>
    request<PageOf<Instrument>>(`/instruments${query(params)}`, { signal }),

  /** Die Handelsplätze — knapp neunzig Zeilen, eine Seite. */
  exchanges: (params: ExchangeQuery = {}, signal?: AbortSignal) =>
    request<PageOf<Exchange>>(`/exchanges${query(params)}`, { signal }),

  /** Die Rohstoffe — elf Zeilen, von Hand gepflegt. */
  commodities: (params: CommodityQuery = {}, signal?: AbortSignal) =>
    request<PageOf<Commodity>>(`/commodities${query(params)}`, { signal }),

  /** Das Vokabular, gegen das verschlagwortet wird — knapp 300 Zeilen. */
  vocabulary: (params: VocabularyQuery = {}, signal?: AbortSignal) =>
    request<PageOf<VocabularyEntry>>(`/vocabulary${query(params)}`, { signal }),

  /**
   * Die vorhandenen Raum-Norm-Paare, für die Auswahlfelder.
   *
   * Eigener Aufruf und nicht aus der Liste abgeleitet: Die Auswahl soll alle
   * Paare kennen, auch die, die auf der aktuellen Seite nicht vorkommen.
   */
  vocabularyNamespaces: (signal?: AbortSignal) =>
    request<VocabularyNamespace[]>('/vocabulary/namespaces', { signal }),

  /** Die Marktereignisse — /market-events, weil /events der SSE-Strom ist. */
  /** Eine Einschätzung — für den Feed. */
  assessment: (id: number, signal?: AbortSignal) =>
    request<Assessment>(`/assessments/${id}`, { signal }),

  assessments: (params: AssessmentQuery = {}, signal?: AbortSignal) =>
    request<PageOf<Assessment>>(`/assessments${query(params)}`, { signal }),

  rss: (params: RssQuery = {}, signal?: AbortSignal) =>
    request<RssFeed>(`/rss${query(params)}`, { signal }),

  rssSources: (signal?: AbortSignal) =>
    request<RssSource[]>('/rss/sources', { signal }),

  imports: (params: ImportQuery = {}, signal?: AbortSignal) =>
    request<PageOf<ImportRecord>>(`/imports${query(params)}`, { signal }),

  importDetail: (id: number, signal?: AbortSignal) =>
    request<ImportDetail>(`/imports/${id}`, { signal }),

  /** Die Verbindungen des MCP-Zugangs samt Tokenzählern. Nur mit oauth:read. */
  oauthClients: (signal?: AbortSignal) =>
    request<OAuthClientInfo[]>('/oauth/clients', { signal }),

  oauthTokens: (params: OAuthTokenQuery = {}, signal?: AbortSignal) =>
    request<PageOf<OAuthToken>>(`/oauth/tokens${query(params)}`, { signal }),

  fxRates: (params: FxRateQuery = {}, signal?: AbortSignal) =>
    request<PageOf<FxRate>>(`/fx-rates${query(params)}`, { signal }),

  /** Die vorhandenen Paare — aus den Daten, nicht aus einer Liste im Code. */
  fxPairs: (signal?: AbortSignal) =>
    request<string[]>('/fx-rates/pairs', { signal }),

  /** Ein Ereignis — für den Feed, der zu einem Hinweis nachlädt. */
  marketEvent: (id: number, signal?: AbortSignal) =>
    request<MarketEvent>(`/market-events/${id}`, { signal }),

  marketEvents: (params: MarketEventQuery = {}, signal?: AbortSignal) =>
    request<PageOf<MarketEvent>>(`/market-events${query(params)}`, { signal }),

  /** Der Branchenbaum — speist die Auswahlliste des Branchenfilters. */
  industries: (params: IndustryQuery = {}, signal?: AbortSignal) =>
    request<PageOf<Industry>>(`/industries${query(params)}`, { signal }),

  /**
   * Der Baum EINER Norm, mit Zahlen je Knoten.
   *
   * Getrennt von `industries`, weil er anderes kostet: ein Durchgang über alle
   * 67.263 Instrumente. Einmal beim Öffnen der Branchenseite ist das in
   * Ordnung, bei jedem Aufruf des Filters wäre es das nicht.
   */
  industryTree: (scheme: string, signal?: AbortSignal) =>
    request<IndustryOverview>(`/industries/tree${query({ scheme })}`,
                              { signal }),

  masterDataDetail: (id: number, params: { currency?: string[] } = {},
                     signal?: AbortSignal) =>
    request<InstrumentDetail>(`/instruments/${id}${query(params)}`, { signal }),

  watchlist: (params: WatchlistQuery = {}, signal?: AbortSignal) =>
    request<PageOf<WatchlistItem>>(`/watchlist${query(params)}`, { signal }),

  /** Die vorkommenden Gründe — speist die Auswahlliste des Filters. */
  watchlistReasons: (signal?: AbortSignal) =>
    request<string[]>('/watchlist/reasons', { signal }),

  /**
   * Einen Titel aufnehmen.
   *
   * Die einzigen schreibenden Aufrufe der Oberfläche — alles andere trägt der
   * MCP-Zugang ein. Was beobachtet wird, ist eine Auswahl und keine
   * Einschätzung: Die trifft ein Mensch am Schirm.
   */
  watchlistAdd: (isin: string, reasons: string[]) =>
    request<WatchlistEntry>('/watchlist', {
      method: 'POST',
      body: JSON.stringify({ isin, reasons }),
    }),

  /** Die Gründe ERSETZEN — der Dialog schickt den Stand, nicht die Änderung. */
  watchlistSetReasons: (id: number, reasons: string[]) =>
    request<WatchlistEntry>(`/watchlist/${id}/reasons`, {
      method: 'PATCH',
      body: JSON.stringify({ reasons }),
    }),

  /** Ruhen lassen oder wieder aufnehmen. Gelöscht wird nicht. */
  watchlistSetActive: (id: number, active: boolean) =>
    request<WatchlistEntry>(`/watchlist/${id}/active`, {
      method: 'PATCH',
      body: JSON.stringify({ active }),
    }),

  quotes: (params: PriceQuery = {}, signal?: AbortSignal) =>
    request<PageOf<Quote>>(`/quotes${query(params)}`, { signal }),

  priceHistory: (params: PriceQuery = {}, signal?: AbortSignal) =>
    request<PageOf<PriceHistory>>(`/price-history${query(params)}`, { signal }),

  /**
   * Meldungen — nur lesend. Es gibt bewusst kein Gegenstück zum Schreiben:
   * Das übernimmt der MCP-Zugang mit `news:write`, das die Oberfläche nicht hat.
   */
  /**
   * Fälle — nur lesend, aus demselben Grund wie bei den Meldungen: Ein Fall
   * wird recherchiert und über den MCP-Zugang eingetragen, nicht hier erfasst.
   */
  researchCases: (params: ResearchQuery = {}, signal?: AbortSignal) =>
    request<PageOf<ResearchCase>>(`/research/cases${query(params)}`, { signal }),

  researchCase: (id: number, params: { currency?: string[] } = {},
                 signal?: AbortSignal) =>
    request<CaseDetail>(`/research/cases/${id}${query(params)}`, { signal }),

  /** Die vier Kennzahlen über der Liste. */
  researchSummary: (signal?: AbortSignal) =>
    request<ResearchSummary>('/research/summary', { signal }),

  /** Was heute ansteht — über alle Fälle. */
  /**
   * Was ansteht. Ohne `until` nur Heutiges und Überfälliges; mit `until`
   * auch, was bis zu diesem Tag fällig wird.
   */
  researchActions: (params: { limit?: number; until?: string } = {},
                    signal?: AbortSignal) =>
    request<DueAction[]>(`/research/actions${query(params)}`, { signal }),

  /** Anstehende Termine — über alle Fälle. */
  researchCatalysts: (params: { horizon?: number; limit?: number } = {},
                      signal?: AbortSignal) =>
    request<UpcomingCatalyst[]>(`/research/catalysts${query(params)}`, { signal }),

  /** Die jüngsten Bewegungen über alle Fälle, jüngste zuerst. */
  researchUpdates: (params: { since?: string; until?: string; limit?: number } = {},
                    signal?: AbortSignal) =>
    request<CaseMovement[]>(`/research/updates${query(params)}`, { signal }),

  /**
   * Eine einzelne Meldung.
   *
   * Für den Ereignisstrom: Ein Hinweis nennt nur die Kennung. Über die Liste
   * zu gehen wäre unzuverlässig — eine Meldung mit älterem Erscheinungsdatum
   * steht nicht auf der ersten Seite.
   */
  newsItem: (id: number, signal?: AbortSignal) =>
    request<NewsItem>(`/news/${id}`, { signal }),

  news: (params: NewsQuery = {}, signal?: AbortSignal) =>
    request<PageOf<NewsItem>>(`/news${query(params)}`, { signal }),

  /**
   * Der Daily Stretch über die aktiven Einträge der Watchlist.
   *
   * OHNE Parameter: Was bewertet wird, steht auf der Watchlist; an welchem
   * Platz, am Instrument. Beides weiß der Dienst — von hier etwas
   * mitzugeben hieße, die Antwort von der Frage abhängig zu machen statt vom
   * Bestand.
   *
   * Gerechnet wird bei jedem Abruf; es gibt keine gespeicherten Ergebnisse.
   */
  /**
   * Buy the Dip — der Boden-Score aus POC v1.
   *
   * `currency` ändert NICHT die Bewertung, nur die Anzeige. Der Score rechnet
   * weiter in Dollar, weil seine Schwellen Dollarbeträge sind („ab 10 Mrd.");
   * umgerechnet wird das Jahreshoch, also ein Kurs, den es an der Börse gab.
   */
  buyTheDip: (params: { currency?: string[] } = {}, signal?: AbortSignal) =>
    request<DipReport>(`/reports/buy-the-dip${query(params)}`, { signal }),

  dailyStretch: (params: { currency?: string[] } = {},
                 signal?: AbortSignal) =>
    request<Report>(`/reports/daily-stretch${query(params)}`, { signal }),

  /**
   * Die Handelsideen — nur lesend. Es gibt bewusst kein Gegenstück zum
   * Schreiben: Ein Vorschlag wird recherchiert und über den MCP-Zugang
   * eingetragen, nicht hier erfasst.
   */
  tradeIdeas: (params: TradeIdeaQuery = {}, signal?: AbortSignal) =>
    request<PageOf<TradeIdea>>(`/trade-ideas${query(params)}`, { signal }),

  tradeIdea: (id: number, params: { currency?: string[] } = {},
              signal?: AbortSignal) =>
    request<TradeIdeaDetail>(`/trade-ideas/${id}${query(params)}`, { signal }),

  /** Eine Entwicklung — vom Schirm aus vor allem das Abschließen. */
  tradeIdeaAddUpdate: (id: number, update: IdeaUpdateInput,
                       params: { currency?: string[] } = {}) =>
    request<TradeIdeaDetail>(`/trade-ideas/${id}/updates${query(params)}`, {
      method: 'POST',
      body: JSON.stringify(update),
    }),

  // --------------------------------------------------------------- Depot

  /** Alle Depots mit ihrer Kasse, nach Namen. */
  depots: (signal?: AbortSignal) =>
    request<DepotSummary[]>('/depots', { signal }),

  /** Ein Depot anlegen. Die Währung steht danach fest. */
  depotCreate: (depot: DepotInput & { currency: string }) =>
    request<Depot>('/depots', {
      method: 'POST',
      body: JSON.stringify(depot),
    }),

  /** Ein Depot: Kasse, offene Orders, Positionen, Kontoauszug. */
  depot: (id: number, signal?: AbortSignal) =>
    request<DepotView>(`/depots/${id}`, { signal }),

  /** Name, Einlage und Gebühr setzen. */
  depotConfigure: (id: number, depot: DepotInput) =>
    request<Depot>(`/depots/${id}`, {
      method: 'PUT',
      body: JSON.stringify(depot),
    }),

  /** Ein leeres Depot löschen. */
  depotDelete: (id: number) =>
    request<void>(`/depots/${id}`, { method: 'DELETE' }),

  /** Orders eines Depots, offene zuerst — die Historie mit Filter. */
  orders: (depotId: number,
           params: { status?: OrderStatus; idea_id?: number } = {},
           signal?: AbortSignal) =>
    request<Order[]>(`/depots/${depotId}/orders${query(params)}`, { signal }),

  orderPlace: (depotId: number, order: OrderInput) =>
    request<Order>(`/depots/${depotId}/orders`, {
      method: 'POST',
      body: JSON.stringify(order),
    }),

  orderCancel: (id: number, reason: string) =>
    request<Order>(`/orders/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /**
   * Was der Broker ausgeführt hat — gebucht vom Menschen am Schirm. Die
   * Antwort ist die Transaktion; die Seite lädt danach das Depot neu.
   */
  orderExecute: (id: number, ausfuehrung: ExecuteInput) =>
    request<Transaction>(`/orders/${id}/execute`, {
      method: 'POST',
      body: JSON.stringify(ausfuehrung),
    }),

  /** Die Ausführung zurücknehmen: Transaktion weg, Order wieder offen. */
  orderRevert: (id: number) =>
    request<Order>(`/orders/${id}/revert`, { method: 'POST' }),

  /** Handel ohne Order nachtragen — in ein bestimmtes Depot. */
  transactionAdd: (depotId: number, transaktion: TransactionInput) =>
    request<Transaction>(`/depots/${depotId}/transactions`, {
      method: 'POST',
      body: JSON.stringify(transaktion),
    }),

  /** Zurücknehmen, nicht ändern: Ein Tippfehler ist keine Tatsache. */
  transactionDelete: (id: number) =>
    request<void>(`/transactions/${id}`, { method: 'DELETE' }),
}
